import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { attemptApi, type AttemptQuestion, type AttemptView } from "@/lib/attemptApi";
import { ProctorController } from "@/proctor/ProctorController";
import { ExamLogo } from "@/components/ExamLogo";

type WarningKind = "fullscreen" | "tabSwitch" | "dualScreen" | null;
type PauseKind = "camera" | null;

export default function Attempt() {
  const { quizId = "", attemptId = "" } = useParams();
  const navigate = useNavigate();

  const [view, setView] = useState<AttemptView | null>(null);
  const [idx, setIdx] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [preflightError, setPreflightError] = useState<string | null>(null);
  const [warning, setWarning] = useState<WarningKind>(null);
  const [pause, setPause] = useState<PauseKind>(null);
  const [pauseError, setPauseError] = useState<string | null>(null);
  // Controller in state (not ref) so setting it triggers a re-render past the pre-start gate.
  const [proctor, setProctor] = useState<ProctorController | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Start / resume the attempt.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const v = await attemptApi.start(attemptId);
        if (cancelled) return;
        setView(v);
        setSecondsLeft(Math.max(0, Math.floor((new Date(v.endsAt).getTime() - Date.now()) / 1000)));
      } catch (e: any) {
        setError(e?.response?.data?.detail ?? "Could not start attempt.");
      }
    })();
    return () => { cancelled = true; };
  }, [attemptId]);

  // Countdown — pauses while a camera-loss modal is up so candidates aren't penalized for the outage.
  useEffect(() => {
    if (!view || pause) return;
    const t = setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [view, pause]);

  // Server heartbeat.
  useEffect(() => {
    if (!view) return;
    const interval = Math.max(10, view.proctor.heartbeatSeconds) * 1000;
    const t = setInterval(async () => {
      try {
        const hb = await attemptApi.heartbeat(attemptId);
        setSecondsLeft(hb.secondsRemaining);
      } catch { /* proctor outbox retries handle transient failure */ }
    }, interval);
    return () => clearInterval(t);
  }, [view, attemptId]);

  // Auto-submit on timeout.
  useEffect(() => {
    if (!view) return;
    if (secondsLeft <= 0 && !submitting) submit(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondsLeft]);

  // Kill the proctor on unmount.
  useEffect(() => () => proctor?.stop(), [proctor]);

  async function beginProctoredExam() {
    if (!view) return;
    setPreflightError(null);
    // Fullscreen + copy/paste listeners attach to the document root so they cover the whole page,
    // including the pre-start gate which doesn't carry containerRef.
    const container = document.documentElement;
    const controller = new ProctorController(attemptId, view.proctor, container, {
      onViolation: (type) => {
        if (type === "FullscreenExit") setWarning("fullscreen");
        else if (type === "TabSwitch" || type === "WindowBlur") setWarning("tabSwitch");
        else if (type === "DualScreenDetected") setWarning("dualScreen");
      },
      onCameraLost: () => setPause("camera"),
      onTerminate: () => submit(true),
    });
    try {
      await controller.preflight();
      await controller.start();
      setProctor(controller);
    } catch (e: any) {
      setPreflightError(e?.message ?? "Could not enter exam mode.");
    }
  }

  async function selectOption(optionId: string) {
    if (!view) return;
    const question = view.questions[idx];
    const prev = question.selectedOptionId;
    setView({ ...view, questions: view.questions.map((x, i) =>
      i === idx ? { ...x, selectedOptionId: optionId } : x) });
    try {
      await attemptApi.select(attemptId, question.attemptQuestionId, optionId);
    } catch (e: any) {
      setView({ ...view, questions: view.questions.map((x, i) =>
        i === idx ? { ...x, selectedOptionId: prev } : x) });
      setError(e?.response?.data?.detail ?? "Autosave failed.");
    }
  }

  async function submit(auto = false) {
    if (submitting) return;
    setSubmitting(true);
    proctor?.stop();
    try {
      await attemptApi.submit(attemptId);
      navigate(`/quiz/${quizId}/result/${attemptId}${auto ? "?auto=1" : ""}`);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? "Submit failed.");
      setSubmitting(false);
    }
  }

  const [fullscreenRetryError, setFullscreenRetryError] = useState<string | null>(null);

  /**
   * Unified dismiss for every warning modal (fullscreen exit, tab switch, dual screen).
   * If the quiz requires fullscreen and we're currently not in it, re-enter — otherwise
   * the candidate would silently continue in windowed mode after any modal.
   */
  async function dismissWarning() {
    setFullscreenRetryError(null);
    const needsFullscreen = view?.proctor.fullscreenEnforce ?? false;
    if (needsFullscreen && !document.fullscreenElement) {
      try {
        await document.documentElement.requestFullscreen();
      } catch (e: any) {
        setFullscreenRetryError(
          e?.message ??
            "Your browser blocked full-screen. Click Resume Quiz again — the click must be fresh.",
        );
        return; // keep the modal up so the user can retry
      }
    }
    setWarning(null);
  }

  const timer = useMemo(() => {
    const m = Math.floor(secondsLeft / 60);
    const s = secondsLeft % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }, [secondsLeft]);

  if (error && !view) return <Centered>{error}</Centered>;
  if (!view) return <Centered>Loading paper…</Centered>;

  // Pre-start gate: user must click a button to grant fullscreen + camera.
  if (!proctor) {
    return (
      <>
        <ExamLogo />
        <main className="mx-auto max-w-lg p-6">
          <div className="card p-8 text-center">
            <h1 className="text-2xl font-bold text-brand-ink">Ready to begin</h1>
            <p className="mt-2 text-sm text-brand-ink/70">
              Clicking below enters full-screen mode and (if enabled) starts your camera. Keep this tab
              in focus. Do not switch tabs or windows during the exam.
            </p>
            {preflightError && <div className="mt-4 text-sm text-brand">{preflightError}</div>}
            <button className="btn-primary mt-6" onClick={beginProctoredExam}>Start proctored exam →</button>
          </div>
        </main>
      </>
    );
  }

  const q: AttemptQuestion = view.questions[idx];

  return (
    <div ref={containerRef} className="min-h-screen bg-brand-light">
      <ExamLogo />
      <main className="mx-auto max-w-3xl p-6">
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <button className="btn-secondary" disabled={idx === 0} onClick={() => setIdx(idx - 1)}>← Previous</button>
            <div className="rounded-pill bg-brand-light px-4 py-1 text-sm text-brand-ink">
              {idx + 1} / {view.questions.length}
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-brand">{timer}</div>
              <div className="text-xs text-brand-ink/60">Time Left</div>
            </div>
          </div>

          <h2 className="mt-6 text-lg font-semibold text-brand-ink">{q.text}</h2>

          <fieldset className="mt-4 space-y-3" aria-labelledby={`q-${q.attemptQuestionId}`}>
            <legend id={`q-${q.attemptQuestionId}`} className="sr-only">Choose an answer</legend>
            {q.options.map((o) => {
              const selected = q.selectedOptionId === o.id;
              return (
                <label
                  key={o.id}
                  className={`flex min-h-[48px] cursor-pointer items-center gap-3 rounded-full border px-5 py-3 transition
                    focus-within:ring-2 focus-within:ring-brand
                    ${selected ? "border-brand bg-brand-light" : "border-brand-ink/20 hover:bg-brand-light"}`}
                >
                  <input type="radio" name={q.attemptQuestionId} className="accent-brand"
                    checked={selected} onChange={() => selectOption(o.id)}
                    aria-label={o.text} />
                  <span className="text-brand-ink">{o.text}</span>
                </label>
              );
            })}
          </fieldset>

          {error && <div className="mt-4 text-sm text-brand">{error}</div>}

          <div className="mt-8 flex justify-between">
            {idx < view.questions.length - 1 ? (
              <button className="btn-primary" onClick={() => setIdx(idx + 1)}>Next →</button>
            ) : (
              <button className="btn-primary" onClick={() => submit(false)} disabled={submitting}>
                {submitting ? "Submitting…" : "Submit exam"}
              </button>
            )}
          </div>
        </div>
      </main>

      {warning && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-bold text-brand">
              {warning === "fullscreen" && "You cannot minimize full screen mode!"}
              {warning === "tabSwitch" && "You cannot switch tabs while taking this quiz!"}
              {warning === "dualScreen" && "Multiple displays detected"}
            </h3>
            <p className="mt-2 text-sm text-brand-ink/80">
              {warning === "fullscreen" &&
                "You are not allowed to minimize full screen while taking this quiz. This violation has been recorded. Click Resume Quiz to return to full screen and continue."}
              {warning === "tabSwitch" &&
                "You are not allowed to switch tabs while taking this quiz. This violation has been recorded. Click Resume Quiz to continue in full screen."}
              {warning === "dualScreen" &&
                "A second display was connected. Disconnect additional displays. If this persists your attempt may be terminated."}
            </p>
            {fullscreenRetryError && (
              <p className="mt-3 text-sm text-brand">{fullscreenRetryError}</p>
            )}
            <div className="mt-6 flex justify-end">
              <button className="btn-primary" onClick={dismissWarning}>Resume Quiz</button>
            </div>
          </div>
        </div>
      )}

      {pause === "camera" && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-bold text-brand">Camera disconnected</h3>
            <p className="mt-2 text-sm text-brand-ink/80">
              Your camera stopped or was disallowed. The exam is paused. Reconnect the
              camera (or re-grant access) and click <b>Retry</b> to continue, or end the
              exam now to submit with your current answers.
            </p>
            {pauseError && <div className="mt-2 text-sm text-brand">{pauseError}</div>}
            <div className="mt-6 flex flex-wrap justify-end gap-2">
              <button className="btn-secondary text-brand" onClick={() => submit(true)}>
                End exam
              </button>
              <button
                className="btn-primary"
                onClick={async () => {
                  setPauseError(null);
                  const ok = await proctor?.restartCamera();
                  if (ok) setPause(null);
                  else setPauseError("Camera still not available. Check the browser permission and try again.");
                }}>
                Retry camera
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return <main className="grid min-h-screen place-items-center p-6 text-brand-ink/60">{children}</main>;
}
